// client/app/boot — the ONE file that wires module facades.
// Per plan §7: critical file.

import * as render from '../render/index.js';
import * as input from '../input/index.js';
import * as world from '../world/index.js';
import * as store from '../local-store/index.js';
import * as ui from '../ui/index.js';
import * as audio from '../audio/index.js';
import { combat, crafting, items, loot, mastery } from '@tiermark/shared';

type WeaponId = 'sword' | 'bow' | 'fireStaff';
interface WeaponAuto {
  baseDamage: number;
  damageType: combat.DamageType;
  rangeM: number;
  intervalMs: number;
  vfx: 'slash' | 'arrow' | 'fireBurst';
}
const WEAPON_AUTOS: Record<WeaponId, WeaponAuto> = {
  sword: { baseDamage: 32, damageType: 'Physical', rangeM: 2.4, intervalMs: 1100, vfx: 'slash' },
  bow: { baseDamage: 28, damageType: 'Physical', rangeM: 16, intervalMs: 1300, vfx: 'arrow' },
  fireStaff: { baseDamage: 26, damageType: 'Magic', rangeM: 14, intervalMs: 1300, vfx: 'fireBurst' },
};
const WOLF_AUTO: combat.AttackSpec = { baseDamage: 14, damageType: 'Physical' };

// Quality multiplier applied to *all* stats of an equipped item (hpBonus, damageBonus, resistances).
const QUALITY_MULT: Record<items.Item['quality'], number> = {
  Normal: 1.0,
  Good: 1.05,
  Outstanding: 1.1,
  Excellent: 1.18,
  Masterpiece: 1.3,
};

function isStackableKind(kind: items.ItemKind): boolean {
  return kind === 'resource' || kind === 'refined';
}

const CHAR_ID = 'local';
const MOVE_SPEED_M_PER_S = 6;
const SAVE_INTERVAL_MS = 2000;

const PLAYER_BASE_HP = 200;
const PLAYER_RESPAWN_MS = 4000;

type EquipSlot = 'mainHand' | 'offHand' | 'head' | 'chest' | 'shoes' | 'cape';
type Equipment = Record<EquipSlot, items.Item | null>;

interface PlayerState {
  name: string;
  pos: { x: number; y: number; z: number };
  axis: { x: number; z: number };
  target: { x: number; z: number } | null;
  hp: number;
  hpMax: number;
  resistances: { Physical: number; Magic: number };
  damageBonus: number;
  enemyTargetId: string | null;
  gatherTargetId: string | null;
  lastGatherTickMs: number;
  lastAutoAttackMs: number;
  silver: number;
  inventory: items.Item[];
  equipment: Equipment;
  fame: { combat: number; gather: number; craft: number };
  dead: boolean;
  respawnAtMs: number;
}

const wolfBaseLootTable: loot.LootTable = {
  id: 'wolf.t2',
  rolls: 1,
  entries: [
    { defId: 'wolfPelt', weight: 7, minStack: 1, maxStack: 2, tier: 2 },
    { defId: 'wolfFang', weight: 2, minStack: 1, maxStack: 1, tier: 2 },
    { defId: 'silverShard', weight: 1, minStack: 1, maxStack: 1, tier: 2 },
  ],
};

const wolfRareLootTable: loot.LootTable = {
  id: 'wolf.rare',
  rolls: 1,
  entries: [
    { defId: 'leather.helm.t2', weight: 4, minStack: 1, maxStack: 1, tier: 2 },
    { defId: 'leather.shoes.t2', weight: 4, minStack: 1, maxStack: 1, tier: 2 },
    { defId: 'leather.chest.t2', weight: 2, minStack: 1, maxStack: 1, tier: 2 },
    { defId: 'cloth.helm.t2', weight: 3, minStack: 1, maxStack: 1, tier: 2 },
    { defId: 'cloth.shoes.t2', weight: 3, minStack: 1, maxStack: 1, tier: 2 },
    { defId: 'plate.helm.t2', weight: 2, minStack: 1, maxStack: 1, tier: 2 },
    { defId: 'plate.shoes.t2', weight: 2, minStack: 1, maxStack: 1, tier: 2 },
    { defId: 'bow.t2', weight: 1, minStack: 1, maxStack: 1, tier: 2 },
    { defId: 'fireStaff.t2', weight: 1, minStack: 1, maxStack: 1, tier: 2 },
  ],
};
const WOLF_RARE_DROP_CHANCE = 0.25;

const seed = (Math.random() * 0xffffffff) >>> 0;
const rng = loot.seededRng(seed);

export async function boot(): Promise<void> {
  console.info('[tiermark] boot: start');
  const canvasHost = document.getElementById('canvas-root');
  if (!canvasHost) throw new Error('boot: #canvas-root missing');

  console.info('[tiermark] boot: migrate IDB');
  await store.migrate();

  console.info('[tiermark] boot: mount renderer + zone');
  render.mount(canvasHost);
  const zone = world.loadZone('greenshade');
  render.addEntity({ id: 'zone:greenshade', object: zone.root });

  console.info('[tiermark] boot: load character');
  let saved: Awaited<ReturnType<typeof store.loadAnyCharacter>> = null;
  try {
    saved = await store.loadAnyCharacter();
  } catch (err) {
    console.warn('[tiermark] boot: loadAnyCharacter failed, starting fresh:', err);
  }

  function freshStartingItems(): items.Item[] {
    return [
      items.createFromDef('bow.t2'),
      items.createFromDef('fireStaff.t2'),
      items.createFromDef('leather.chest.t2'),
    ];
  }
  function freshStartingEquipment(): Equipment {
    return {
      mainHand: items.createFromDef('sword.t2'),
      offHand: null,
      head: null,
      chest: null,
      shoes: null,
      cape: null,
    };
  }

  const player: PlayerState = {
    name: saved?.name ?? 'Wanderer',
    pos: saved
      ? saved.position
      : { x: zone.spawnPos.x, y: zone.spawnPos.y, z: zone.spawnPos.z },
    axis: { x: 0, z: 0 },
    target: null,
    hp: PLAYER_BASE_HP,
    hpMax: PLAYER_BASE_HP,
    resistances: { Physical: 0, Magic: 0 },
    damageBonus: 0,
    enemyTargetId: null,
    gatherTargetId: null,
    lastGatherTickMs: 0,
    lastAutoAttackMs: 0,
    silver: saved?.silver ?? 0,
    inventory: saved?.inventory ?? freshStartingItems(),
    equipment: saved?.equipment
      ? {
          mainHand: saved.equipment.mainHand ?? null,
          offHand: saved.equipment.offHand ?? null,
          head: saved.equipment.head ?? null,
          chest: saved.equipment.chest ?? null,
          shoes: saved.equipment.shoes ?? null,
          cape: saved.equipment.cape ?? null,
        }
      : freshStartingEquipment(),
    fame: saved?.fame ?? { combat: 0, gather: 0, craft: 0 },
    dead: false,
    respawnAtMs: 0,
  };
  player.pos.y = zone.heightAt(player.pos.x, player.pos.z);
  recomputeStats();

  const characterRig = render.makeCharacter({
    tunic: '#3d6a8a',
    pants: '#3a2820',
    skin: '#e6c0a0',
  });
  characterRig.group.position.set(player.pos.x, player.pos.y, player.pos.z);
  render.addEntity({ id: 'player', object: characterRig.group });
  render.setCameraTarget(player.pos);

  const playerHpBar = ui.createFloatingBar('#5ac070');

  const enemies = world.spawnInitialEnemies(zone);
  interface EnemyVisual {
    rig: render.WolfRig;
    bar: ui.FloatingBar;
  }
  const enemyVisuals = new Map<string, EnemyVisual>();
  for (const e of enemies) {
    const rig = render.makeWolf();
    rig.group.position.set(e.pos.x, e.pos.y, e.pos.z);
    rig.group.rotation.y = e.yaw;
    render.addEntity({ id: e.id, object: rig.group });
    const bar = ui.createFloatingBar('#c83030');
    enemyVisuals.set(e.id, { rig, bar });
  }

  // Resource nodes
  const nodes = world.spawnInitialResourceNodes(zone);
  interface NodeVisual {
    rig: render.ResourceNodeRig;
  }
  const nodeVisuals = new Map<string, NodeVisual>();
  for (const n of nodes) {
    const rig = render.makeResourceNode(n.kind, n.tier);
    rig.group.position.set(n.pos.x, n.pos.y, n.pos.z);
    rig.group.rotation.y = Math.random() * Math.PI * 2;
    render.addEntity({ id: n.id, object: rig.group });
    nodeVisuals.set(n.id, { rig });
  }

  // Crafting stations near spawn
  interface Station {
    id: string;
    kind: crafting.StationKind;
    label: string;
    pos: { x: number; y: number; z: number };
  }
  const stationDefs: Array<{ kind: crafting.StationKind; label: string; offset: { x: number; z: number } }> = [
    { kind: 'refiner', label: 'Refining Station', offset: { x: -4, z: -2 } },
    { kind: 'weaponsmith', label: 'Weaponsmith', offset: { x: 0, z: -4 } },
    { kind: 'armorer', label: 'Armorer', offset: { x: 4, z: -2 } },
  ];
  const stations: Station[] = stationDefs.map((d) => {
    const x = zone.spawnPos.x + d.offset.x;
    const z = zone.spawnPos.z + d.offset.z;
    const pos = { x, y: zone.heightAt(x, z), z };
    const id = `station-${d.kind}`;
    const group = render.makeStation(d.kind);
    group.position.set(pos.x, pos.y, pos.z);
    render.addEntity({ id, object: group });
    return { id, kind: d.kind, label: d.label, pos };
  });

  const gatherBar = ui.createGatherBar();
  const GATHER_RANGE = 2.6;
  const GATHER_TICK_MS = 700;

  let activeStationId: string | null = null;
  let nearbyStation: Station | null = null;
  const STATION_RANGE = 3.4;

  // ---- Inventory helpers (operate on Item instances) ----

  function inventoryAddItem(item: items.Item): void {
    if (isStackableKind(item.kind)) {
      const existing = player.inventory.find(
        (it) =>
          it.defId === item.defId &&
          it.quality === item.quality &&
          it.enchant === item.enchant &&
          isStackableKind(it.kind)
      );
      if (existing) {
        existing.stack += item.stack;
        return;
      }
    }
    player.inventory.push(item);
  }

  function inventoryRemoveById(itemId: string): items.Item | null {
    const idx = player.inventory.findIndex((it) => it.id === itemId);
    if (idx < 0) return null;
    const removed = player.inventory.splice(idx, 1)[0] ?? null;
    return removed;
  }

  // Consume `count` units of `defId` from inventory, draining lowest-quality stacks first.
  // Returns true if fully consumed.
  function inventoryRemoveByDef(defId: string, count: number): boolean {
    if (count <= 0) return true;
    if (inventoryCountByDef(defId) < count) return false;
    const QUALITY_ORDER: Record<items.Item['quality'], number> = {
      Normal: 0, Good: 1, Outstanding: 2, Excellent: 3, Masterpiece: 4,
    };
    const matches = player.inventory
      .map((it, i) => ({ it, i }))
      .filter(({ it }) => it.defId === defId)
      .sort((a, b) => QUALITY_ORDER[a.it.quality] - QUALITY_ORDER[b.it.quality]);
    let remaining = count;
    const toRemove: number[] = [];
    for (const { it, i } of matches) {
      if (remaining <= 0) break;
      const take = Math.min(it.stack, remaining);
      it.stack -= take;
      remaining -= take;
      if (it.stack <= 0) toRemove.push(i);
    }
    // Splice in descending index order so earlier removals don't shift later ones.
    toRemove.sort((a, b) => b - a);
    for (const i of toRemove) player.inventory.splice(i, 1);
    return true;
  }

  function inventoryCountByDef(defId: string): number {
    let n = 0;
    for (const it of player.inventory) {
      if (it.defId === defId) n += it.stack;
    }
    return n;
  }

  function aggregatedCounts(): Map<string, number> {
    const m = new Map<string, number>();
    for (const it of player.inventory) {
      m.set(it.defId, (m.get(it.defId) ?? 0) + it.stack);
    }
    return m;
  }

  const craftingPanel = ui.createCraftingPanel({
    onCraft(recipeId): void {
      const recipe = crafting.recipeById(recipeId);
      if (!recipe) return;
      const counts = aggregatedCounts();
      if (!crafting.canAfford(recipe, counts)) {
        ui.notify('Not enough materials', 1000);
        return;
      }
      for (const ing of recipe.ingredients) {
        inventoryRemoveByDef(ing.defId, ing.count);
      }
      const fameTotal = player.fame.combat + player.fame.gather + player.fame.craft;
      const masteryLevel = mastery.levelFor(fameTotal);
      const craftSeed = (Math.random() * 0xffffffff) >>> 0;
      const quality = crafting.rollQuality(craftSeed, recipe, masteryLevel);
      const outDef = items.defOf(recipe.outputDefId);
      if (outDef) {
        if (isStackableKind(outDef.kind)) {
          // Stackable output: produce `outputCount` units in one stack.
          inventoryAddItem(items.createFromDef(recipe.outputDefId, { quality, stack: recipe.outputCount }));
        } else {
          // Gear-like: each unit is its own instance.
          for (let i = 0; i < recipe.outputCount; i++) {
            inventoryAddItem(items.createFromDef(recipe.outputDefId, { quality }));
          }
        }
      }
      player.fame.craft += mastery.gainFor({ itemDefId: recipe.outputDefId, action: 'craft' });
      audio.play('loot');
      const qualityNote =
        outDef && !isStackableKind(outDef.kind) ? ` (${quality})` : '';
      ui.notify(`Crafted ${outDef?.name ?? recipe.outputDefId}${qualityNote}`, 2200);
      refreshPanel();
      refreshCraftingPanel();
      updateHud();
    },
    onClose(): void {
      activeStationId = null;
      craftingPanel.setOpen(false);
    },
  });

  function refreshCraftingPanel(): void {
    if (!activeStationId) {
      craftingPanel.setOpen(false);
      return;
    }
    const station = stations.find((s) => s.id === activeStationId);
    if (!station) return;
    const recipes = crafting.recipesAtStation(station.kind);
    const counts = aggregatedCounts();
    const rows: ui.CraftingRecipeRow[] = recipes.map((r) => {
      const outDef = items.defOf(r.outputDefId);
      return {
        recipeId: r.id,
        outputName: outDef?.name ?? r.outputDefId,
        outputIconColor: outDef?.iconColor ?? '#888',
        outputDescription: outDef?.description ?? '',
        outputTier: r.outputTier,
        ingredients: r.ingredients.map((i) => {
          const def = items.defOf(i.defId);
          return {
            defId: i.defId,
            name: def?.name ?? i.defId,
            iconColor: def?.iconColor ?? '#888',
            have: counts.get(i.defId) ?? 0,
            need: i.count,
          };
        }),
        craftingFame: r.craftingFame,
        craftMs: r.craftMs,
        affordable: crafting.canAfford(r, counts),
      };
    });
    craftingPanel.setRecipes(station.label, rows);
    craftingPanel.setOpen(true);
  }

  const inventoryPanel = ui.createInventoryPanel({
    onSlotClick(itemId: string): void {
      const item = player.inventory.find((it) => it.id === itemId);
      if (!item) return;
      const def = items.defOf(item.defId);
      if (!def) return;
      if (def.kind === 'resource' || def.kind === 'refined') {
        ui.notify(`${def.name} is a material — used for crafting.`, 1200);
        return;
      }
      if (def.slot && (def.kind === 'weapon' || def.kind === 'armor')) {
        equipItemById(itemId);
      }
    },
    onEquipSlotClick(slot): void {
      unequipItem(slot);
    },
  });

  // Sum equipment stats, applying per-item quality multiplier to everything in its stat block.
  // Accepts an alternate equipment map so tooltips can preview a hypothetical swap (Workstream F).
  function statsOf(eq: Equipment): { hpBonus: number; physRes: number; magRes: number; damageBonus: number } {
    let hpBonus = 0;
    let physRes = 0;
    let magRes = 0;
    let damageBonus = 0;
    for (const slot of ['mainHand', 'offHand', 'head', 'chest', 'shoes', 'cape'] as const) {
      const it = eq[slot];
      if (!it) continue;
      const def = items.defOf(it.defId);
      if (!def) continue;
      const m = QUALITY_MULT[it.quality];
      hpBonus += def.stats.hpBonus * m;
      damageBonus += def.stats.damageBonus * m;
      physRes += (def.stats.resistances.Physical ?? 0) * m;
      magRes += (def.stats.resistances.Magic ?? 0) * m;
    }
    return { hpBonus, physRes, magRes, damageBonus };
  }

  function recomputeStats(): void {
    const s = statsOf(player.equipment);
    const newMax = PLAYER_BASE_HP + Math.round(s.hpBonus);
    const ratio = player.hpMax > 0 ? player.hp / player.hpMax : 1;
    player.hpMax = newMax;
    player.hp = Math.round(Math.max(1, Math.min(newMax, ratio * newMax)));
    player.resistances = { Physical: s.physRes, Magic: s.magRes };
    player.damageBonus = Math.round(s.damageBonus);
  }

  function getEquippedWeaponId(): string | null {
    const it = player.equipment.mainHand;
    if (!it) return null;
    const def = items.defOf(it.defId);
    return def?.weaponId ?? null;
  }

  function getCurrentAutoAttack(): WeaponAuto | null {
    const wid = getEquippedWeaponId();
    if (!wid) return null;
    const base = WEAPON_AUTOS[wid as WeaponId];
    if (!base) return null;
    return { ...base, baseDamage: base.baseDamage + player.damageBonus };
  }

  function equipItemById(itemId: string): void {
    const item = player.inventory.find((it) => it.id === itemId);
    if (!item) return;
    const def = items.defOf(item.defId);
    if (!def || !def.slot) return;
    if (def.kind !== 'weapon' && def.kind !== 'armor') return;
    const slot = def.slot as EquipSlot;
    inventoryRemoveById(itemId);
    const prev = player.equipment[slot];
    player.equipment[slot] = item;
    if (prev) inventoryAddItem(prev);
    audio.play('loot');
    recomputeStats();
    refreshPanel();
    updateHud();
    ui.notify(`Equipped ${def.name}${item.quality !== 'Normal' ? ` (${item.quality})` : ''}`, 1100);
  }

  function unequipItem(slot: EquipSlot): void {
    const cur = player.equipment[slot];
    if (!cur) return;
    player.equipment[slot] = null;
    inventoryAddItem(cur);
    audio.play('loot');
    recomputeStats();
    refreshPanel();
    updateHud();
  }

  function refreshPanel(): void {
    const equipRows: ui.EquipmentSlotRow[] = (['mainHand', 'offHand', 'head', 'chest', 'shoes', 'cape'] as const).map(
      (slot) => {
        const it = player.equipment[slot];
        const def = it ? items.defOf(it.defId) : null;
        return {
          slot,
          label: SLOT_LABELS[slot],
          defId: it?.defId ?? null,
          name: def?.name ?? null,
          iconColor: def?.iconColor ?? null,
        };
      }
    );
    inventoryPanel.setEquipment(equipRows);

    const cur = statsOf(player.equipment);
    const rows: ui.InventoryRow[] = [];
    for (const it of player.inventory) {
      const def = items.defOf(it.defId);
      if (!def) continue;
      const slot = def.slot as EquipSlot | null;
      const isEquippable = !!slot && (def.kind === 'weapon' || def.kind === 'armor');
      let statDelta: string | null = null;
      if (isEquippable && slot) {
        const hypothetical: Equipment = { ...player.equipment, [slot]: it };
        const hyp = statsOf(hypothetical);
        statDelta = formatStatDelta(cur, hyp);
      }
      rows.push({
        itemId: it.id,
        defId: def.id,
        name: def.name,
        iconColor: def.iconColor,
        count: it.stack,
        description: def.description,
        equippableSlot: (def.slot as ui.InventoryRow['equippableSlot']) ?? null,
        isEquipped: false, // equipped items live in player.equipment, not inventory.
        tier: def.tier,
        quality: it.quality,
        statDelta,
      });
    }
    rows.sort((a, b) => a.name.localeCompare(b.name));
    inventoryPanel.setRows(rows);
  }

  type Stats = ReturnType<typeof statsOf>;
  function formatStatDelta(cur: Stats, hyp: Stats): string | null {
    const lines: string[] = [];
    const bonus = (v: number): string => (Math.round(v) === 0 ? '—' : `+${Math.round(v)}`);
    const pct = (v: number): string => `${Math.round(v * 100)}%`;
    const dlt = (v: number): string => {
      const n = Math.round(v);
      return n > 0 ? `(+${n})` : `(${n})`;
    };
    const dltPct = (v: number): string => {
      const n = Math.round(v * 100);
      return n > 0 ? `(+${n}%)` : `(${n}%)`;
    };
    if (Math.round(hyp.damageBonus) !== Math.round(cur.damageBonus)) {
      lines.push(`Damage ${bonus(cur.damageBonus)} → ${bonus(hyp.damageBonus)} ${dlt(hyp.damageBonus - cur.damageBonus)}`);
    }
    if (Math.round(hyp.hpBonus) !== Math.round(cur.hpBonus)) {
      lines.push(`HP ${bonus(cur.hpBonus)} → ${bonus(hyp.hpBonus)} ${dlt(hyp.hpBonus - cur.hpBonus)}`);
    }
    if (Math.round(hyp.physRes * 100) !== Math.round(cur.physRes * 100)) {
      lines.push(`Phys Res ${pct(cur.physRes)} → ${pct(hyp.physRes)} ${dltPct(hyp.physRes - cur.physRes)}`);
    }
    if (Math.round(hyp.magRes * 100) !== Math.round(cur.magRes * 100)) {
      lines.push(`Magic Res ${pct(cur.magRes)} → ${pct(hyp.magRes)} ${dltPct(hyp.magRes - cur.magRes)}`);
    }
    return lines.length ? lines.join('\n') : null;
  }

  const SLOT_LABELS: Record<EquipSlot, string> = {
    mainHand: 'Main Hand',
    offHand: 'Off Hand',
    head: 'Head',
    chest: 'Chest',
    shoes: 'Shoes',
    cape: 'Cape',
  };

  function updateHud(): void {
    const wid = getEquippedWeaponId();
    const fameTotal = player.fame.combat + player.fame.gather + player.fame.craft;
    const lvl = mastery.levelFor(fameTotal);
    ui.setHudText(
      [
        `Tiermark — ${player.name}`,
        `Greenshade Forest`,
        `HP: ${player.hp} / ${player.hpMax}`,
        `Silver: ${player.silver}`,
        `Weapon: ${wid ?? '—'}`,
        `Fame: ${fameTotal}  (lvl ${lvl})`,
        `[I] Inventory`,
      ].join('\n')
    );
  }

  refreshPanel();
  updateHud();
  console.info('[tiermark] boot: hiding overlay, starting frame loop');
  ui.hideBootOverlay();
  if (saved) ui.notify('Welcome back, traveler.', 2000);
  ui.notify('Click a wolf to attack. Walk into range; you auto-attack on cooldown. I for inventory. C to craft near a station.', 5500);

  input.install();
  input.subscribe((intent) => {
    if (intent.kind === 'togglePanel') {
      inventoryPanel.toggle();
      if (inventoryPanel.isOpen()) refreshPanel();
      return;
    }
    if (intent.kind === 'craftToggle') {
      if (activeStationId) {
        activeStationId = null;
        craftingPanel.setOpen(false);
      } else if (nearbyStation) {
        activeStationId = nearbyStation.id;
        refreshCraftingPanel();
      }
      return;
    }
    if (player.dead) return;
    switch (intent.kind) {
      case 'moveAxis':
        player.axis = { x: intent.x, z: intent.z };
        if (intent.x !== 0 || intent.z !== 0) {
          player.target = null;
        }
        break;
      case 'moveTo': {
        const aliveEnemyIds = enemies.filter((e) => e.state !== 'dead').map((e) => e.id);
        const picked = render.pickEntity(intent.clientX, intent.clientY, aliveEnemyIds);
        if (picked) {
          player.enemyTargetId = picked;
          player.gatherTargetId = null;
          gatherBar.hide();
          render.setReticleTarget(picked);
          break;
        }
        const aliveNodeIds = nodes.filter((n) => n.state === 'live').map((n) => n.id);
        const pickedNode = render.pickEntity(intent.clientX, intent.clientY, aliveNodeIds);
        if (pickedNode) {
          player.enemyTargetId = null;
          player.gatherTargetId = pickedNode;
          render.setReticleTarget(pickedNode);
          break;
        }
        player.enemyTargetId = null;
        player.gatherTargetId = null;
        gatherBar.hide();
        render.setReticleTarget(null);
        const hit = render.raycastGround(intent.clientX, intent.clientY);
        if (hit) {
          player.target = hit;
          player.axis = { x: 0, z: 0 };
        }
        break;
      }
    }
  });

  function playerCombatant(): combat.Combatant {
    return {
      hp: player.hp,
      hpMax: player.hpMax,
      resistances: player.resistances,
    };
  }

  function enemyCombatant(e: world.Enemy): combat.Combatant {
    return {
      hp: e.hp,
      hpMax: e.hpMax,
      resistances: { Physical: 0.05 },
    };
  }

  function applyDamageToEnemy(target: world.Enemy, attack: combat.AttackSpec): void {
    const result = combat.resolve(enemyCombatant(target), attack, {
      now: performance.now(),
      rng,
    });
    target.hp = result.newDefenderHp;
    if (result.damageDealt > 0) {
      const flashColor = attack.damageType === 'Magic' ? '#ff8030' : '#ffb060';
      render.spawnHitFlash(
        { x: target.pos.x, y: target.pos.y + 0.7, z: target.pos.z },
        flashColor
      );
      audio.play(result.crit ? 'swordCrit' : 'swordHit');
    }
    const screen = render.projectToScreen({ x: target.pos.x, y: target.pos.y + 1.6, z: target.pos.z });
    if (screen && !screen.behind) {
      ui.spawnDamageNumber(screen.x, screen.y, result.damageDealt, result.crit ? 'crit' : 'hit');
    }
    if (result.killed) {
      handleEnemyDeath(target);
    }
  }

  function handleEnemyDeath(e: world.Enemy): void {
    e.state = 'dead';
    e.respawnAtMs = performance.now() + 15_000;
    audio.play('wolfDeath');
    audio.play('loot');
    const visual = enemyVisuals.get(e.id);
    if (visual) {
      render.setEntityVisible(e.id, false);
      visual.bar.update({ screenX: 0, screenY: 0, fillPct: 0, visible: false });
    }
    if (player.enemyTargetId === e.id) {
      player.enemyTargetId = null;
      render.setReticleTarget(null);
    }

    const drops: loot.ItemStack[] = [];
    drops.push(...loot.draw(wolfBaseLootTable, rng));
    if (rng() < WOLF_RARE_DROP_CHANCE) {
      drops.push(...loot.draw(wolfRareLootTable, rng));
    }
    for (const d of drops) {
      if (d.defId === 'silverShard') {
        player.silver += d.count * 5;
        continue;
      }
      const def = items.defOf(d.defId);
      if (!def) continue;
      if (isStackableKind(def.kind)) {
        inventoryAddItem(items.createFromDef(d.defId, { stack: d.count }));
      } else {
        for (let i = 0; i < d.count; i++) {
          inventoryAddItem(items.createFromDef(d.defId));
        }
      }
    }
    player.fame.combat += mastery.gainFor({ itemDefId: 'sword.t2', action: 'combat' });

    const dropsText = drops
      .map((d) => {
        const def = items.defOf(d.defId);
        return `${def?.name ?? d.defId}${d.count > 1 ? ` ×${d.count}` : ''}`;
      })
      .join(', ');
    ui.notify(`Wolf slain — ${dropsText}`, 2500);
    refreshPanel();
    updateHud();
  }

  function applyDamageToPlayer(amount: number, isCrit: boolean): void {
    player.hp = Math.max(0, player.hp - amount);
    audio.play('playerHurt');
    render.spawnHitFlash({ x: player.pos.x, y: player.pos.y + 0.9, z: player.pos.z }, '#ff8060');
    const screen = render.projectToScreen({ x: player.pos.x, y: player.pos.y + 2.0, z: player.pos.z });
    if (screen && !screen.behind) {
      ui.spawnDamageNumber(screen.x, screen.y - 24, amount, isCrit ? 'crit' : 'hit');
    }
    updateHud();
    if (player.hp <= 0) {
      handlePlayerDeath();
    }
  }

  function handlePlayerDeath(): void {
    player.dead = true;
    player.respawnAtMs = performance.now() + PLAYER_RESPAWN_MS;
    render.setEntityVisible('player', false);
    audio.play('playerDeath');
    ui.notify('You have been slain. Respawning…', PLAYER_RESPAWN_MS);
  }

  function respawnPlayer(): void {
    player.dead = false;
    player.hp = player.hpMax;
    player.pos.x = zone.spawnPos.x;
    player.pos.z = zone.spawnPos.z;
    player.pos.y = zone.heightAt(player.pos.x, player.pos.z);
    player.target = null;
    player.axis = { x: 0, z: 0 };
    player.enemyTargetId = null;
    render.setReticleTarget(null);
    characterRig.group.position.set(player.pos.x, player.pos.y, player.pos.z);
    render.setEntityVisible('player', true);
    updateHud();
  }

  let lastSave = performance.now();
  let lastFrame = performance.now();

  function tick(now: number): void {
    const dtMs = Math.min(80, now - lastFrame);
    lastFrame = now;
    const dt = dtMs / 1000;

    const auto = getCurrentAutoAttack();

    if (player.dead) {
      if (now >= player.respawnAtMs) respawnPlayer();
    } else {
      let dx = 0;
      let dz = 0;
      const targetEnemy = player.enemyTargetId
        ? enemies.find((e) => e.id === player.enemyTargetId && e.state !== 'dead') ?? null
        : null;
      if (targetEnemy && auto) {
        const tx = targetEnemy.pos.x - player.pos.x;
        const tz = targetEnemy.pos.z - player.pos.z;
        const dist = Math.hypot(tx, tz);
        if (dist > auto.rangeM - 0.3) {
          dx = tx / dist;
          dz = tz / dist;
        } else if (now - player.lastAutoAttackMs >= auto.intervalMs) {
          player.lastAutoAttackMs = now;
          audio.play('swordSwing');
          if (auto.vfx === 'slash') {
            const facingYaw = Math.atan2(tx, tz);
            render.spawnSlashArc(
              { x: player.pos.x, y: player.pos.y, z: player.pos.z },
              facingYaw,
              2.6
            );
          } else if (auto.vfx === 'arrow') {
            render.spawnArrowStreak(
              { x: player.pos.x, y: player.pos.y + 1.0, z: player.pos.z },
              { x: targetEnemy.pos.x, y: targetEnemy.pos.y + 0.7, z: targetEnemy.pos.z }
            );
          } else if (auto.vfx === 'fireBurst') {
            render.spawnFireBurst(
              { x: targetEnemy.pos.x, y: targetEnemy.pos.y + 0.7, z: targetEnemy.pos.z },
              0.5
            );
          }
          applyDamageToEnemy(targetEnemy, auto);
        }
      } else if (player.gatherTargetId) {
        const node = nodes.find((n) => n.id === player.gatherTargetId);
        if (!node || node.state !== 'live') {
          player.gatherTargetId = null;
          gatherBar.hide();
        } else {
          const tx = node.pos.x - player.pos.x;
          const tz = node.pos.z - player.pos.z;
          const dist = Math.hypot(tx, tz);
          if (dist > GATHER_RANGE) {
            dx = tx / dist;
            dz = tz / dist;
            gatherBar.hide();
          } else {
            // In range — chip on cadence.
            characterRig.group.rotation.y = Math.atan2(tx, tz);
            const def = items.defOf(node.rawDefId);
            gatherBar.show(`${def?.name ?? node.rawDefId}  (${node.kind})`);
            gatherBar.setProgress(1 - node.hp / node.hpMax);
            if (now - player.lastGatherTickMs >= GATHER_TICK_MS) {
              player.lastGatherTickMs = now;
              const result = world.gatherTick(node);
              if (result.damageDealt > 0) {
                audio.play('swordSwing');
                render.spawnHitFlash(
                  { x: node.pos.x, y: node.pos.y + 0.6, z: node.pos.z },
                  '#fff0b0'
                );
                for (const yld of result.yielded) {
                  inventoryAddItem(items.createFromDef(yld.defId, { stack: yld.count }));
                }
                player.fame.gather += mastery.gainFor({
                  itemDefId: node.rawDefId,
                  action: 'gather',
                });
                refreshPanel();
              }
              if (result.depleted) {
                audio.play('loot');
                ui.notify(`Depleted ${def?.name ?? node.rawDefId}`, 1200);
                player.gatherTargetId = null;
                render.setReticleTarget(null);
                gatherBar.hide();
                refreshPanel();
                updateHud();
              }
            }
          }
        }
      } else if (player.target) {
        const tx = player.target.x - player.pos.x;
        const tz = player.target.z - player.pos.z;
        const dist = Math.hypot(tx, tz);
        if (dist < 0.15) player.target = null;
        else {
          dx = tx / dist;
          dz = tz / dist;
        }
      } else {
        dx = player.axis.x;
        dz = player.axis.z;
      }

      // Station proximity → show "[C] Craft" prompt; player must press C to open panel.
      let nearestStation: Station | null = null;
      let nearestDist = Infinity;
      for (const s of stations) {
        const d = Math.hypot(s.pos.x - player.pos.x, s.pos.z - player.pos.z);
        if (d < STATION_RANGE && d < nearestDist) {
          nearestDist = d;
          nearestStation = s;
        }
      }
      const previousNearbyId = nearbyStation?.id ?? null;
      nearbyStation = nearestStation;
      const newNearbyId = nearbyStation?.id ?? null;
      if (newNearbyId !== previousNearbyId) {
        ui.setCraftPrompt(nearbyStation ? `[C] Craft at ${nearbyStation.label}` : null);
        // Close any open panel that doesn't match the new nearby station.
        if (activeStationId && activeStationId !== newNearbyId) {
          activeStationId = null;
          craftingPanel.setOpen(false);
        }
      }
      if (activeStationId) {
        refreshCraftingPanel();
      }

      const moving = dx !== 0 || dz !== 0;
      if (moving) {
        const step = MOVE_SPEED_M_PER_S * dt;
        const half = 48;
        player.pos.x = Math.max(-half, Math.min(half, player.pos.x + dx * step));
        player.pos.z = Math.max(-half, Math.min(half, player.pos.z + dz * step));
        player.pos.y = zone.heightAt(player.pos.x, player.pos.z);
        characterRig.group.position.set(player.pos.x, player.pos.y, player.pos.z);
        characterRig.group.rotation.y = Math.atan2(dx, dz);
      }
      render.tickCharacterIdle(characterRig, now, moving);
    }

    world.updateResourceNodes(nodes, now);

    for (const n of nodes) {
      const v = nodeVisuals.get(n.id);
      if (!v) continue;
      render.tickResourceNode(v.rig, now, n.state === 'depleted');
    }

    world.updateEnemies(
      enemies,
      { x: player.pos.x, z: player.pos.z },
      zone,
      dtMs,
      now,
      (_attacker) => {
        if (player.dead) return;
        audio.play('wolfBite');
        const result = combat.resolve(playerCombatant(), WOLF_AUTO, {
          now,
          rng,
        });
        if (result.damageDealt > 0) {
          applyDamageToPlayer(result.damageDealt, result.crit);
        }
      }
    );

    for (const e of enemies) {
      const visual = enemyVisuals.get(e.id);
      if (!visual) continue;
      if (e.state === 'dead') {
        render.setEntityVisible(e.id, false);
        visual.bar.update({ screenX: 0, screenY: 0, fillPct: 0, visible: false });
        continue;
      }
      render.setEntityVisible(e.id, true);
      render.setEntityTransform(e.id, e.pos, e.yaw);
      render.tickWolf(visual.rig, now, e.state === 'chase' || e.state === 'flee' || e.state === 'idle');
      const screen = render.projectToScreen({ x: e.pos.x, y: e.pos.y + 1.6, z: e.pos.z });
      if (screen) {
        visual.bar.update({
          screenX: screen.x,
          screenY: screen.y,
          fillPct: e.hp / e.hpMax,
          visible: !screen.behind,
          label: e.state === 'flee' ? 'Wolf (fleeing)' : 'Wolf',
          color: e.state === 'flee' ? '#c8a040' : '#c83030',
        });
      }
    }

    const pScreen = render.projectToScreen({ x: player.pos.x, y: player.pos.y + 2.2, z: player.pos.z });
    playerHpBar.update({
      screenX: pScreen?.x ?? 0,
      screenY: pScreen?.y ?? 0,
      fillPct: player.hp / player.hpMax,
      visible: !player.dead && !!pScreen && !pScreen.behind,
      label: player.name,
      color: '#5ac070',
    });

    render.tickReticle(now);
    render.frameVfx(now);
    render.setCameraTarget(player.pos);
    render.frame(dtMs);

    if (now - lastSave > SAVE_INTERVAL_MS) {
      lastSave = now;
      void store.saveCharacter({
        id: CHAR_ID,
        name: player.name,
        position: { ...player.pos },
        zoneId: 'greenshade',
        createdAt: saved?.createdAt ?? Date.now(),
        updatedAt: Date.now(),
        silver: player.silver,
        inventory: player.inventory,
        equipment: { ...player.equipment },
        fame: { ...player.fame },
      });
    }

    requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
}
